/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useQuery } from '@tanstack/react-query';
import { ClientsService } from '@services/requests/housien-services/ClientsService';

interface Contact {
  type?: string;
  value?: string;
}

interface OwnerRecord {
  personal_info?: { full_name?: string };
  identity?: { corporate_name?: string };
  assignment?: { contacts?: Contact[] };
}

export interface PropertyOwnerContact {
  /** Display name, when the owner resolves to a client on file. */
  name?: string;
  /** The phone exactly as stored on the client, for display. */
  phoneDisplay?: string;
  /** Digits-only phone, ready for a `tel:` href. */
  phone?: string;
  /** Digits-only number for WhatsApp, or an e-mail when no number exists. */
  message?: string;
  /** True while the owner record is still loading. */
  loading: boolean;
}

/**
 * Picks the first contact of any of the given types.
 */
function pickContact(contacts: Contact[], types: string[]): string | undefined {
  return contacts.find((c) => c.type && types.includes(c.type))?.value || undefined;
}

/** Digits only, ready for a `tel:` href, or nothing when there is no value. */
function digitsOnly(value?: string): string | undefined {
  return value?.replace(/\D/g, '') || undefined;
}

/**
 * The owner's name and reachable points, read off the client record.
 *
 * @param owner - The client the property names as its owner.
 * @returns The name, the phone (as stored and digits only) and whatever can
 * carry a message.
 */
function toOwnerContact(owner: OwnerRecord): Omit<PropertyOwnerContact, 'loading'> {
  const contacts = owner.assignment?.contacts ?? [];
  const phoneDisplay = pickContact(contacts, ['phone', 'whatsapp']);
  const phone = digitsOnly(phoneDisplay);
  return {
    name: owner.personal_info?.full_name || owner.identity?.corporate_name || undefined,
    phoneDisplay,
    phone,
    message: phone || pickContact(contacts, ['email']),
  };
}

/**
 * Resolves the owner's name and contacts for the ownership card.
 *
 * The owner is stored as a client id, so it is fetched by that id rather than
 * searched for in a page of the agency's clients: an owner outside the first
 * page used to resolve to nothing, leaving the raw id on screen and both
 * actions disabled.
 *
 * @param agencyId - Agency owning both the property and the client.
 * @param ownerClientId - `ownership.owner_client_id` from the property.
 * @returns The owner's name and contact points, plus a loading flag.
 */
export function usePropertyOwnerContact(
  agencyId: string,
  ownerClientId?: string,
): PropertyOwnerContact {
  const query = useQuery({
    queryKey: ['agency', agencyId, 'clients', ownerClientId] as const,
    queryFn: () => ClientsService.getAgencyClient(agencyId, ownerClientId as string),
    enabled: Boolean(agencyId && ownerClientId),
    staleTime: 60 * 1000,
    retry: false,
  });

  if (!ownerClientId) return { loading: false };
  if (!query.data) return { loading: query.isLoading };
  return { ...toOwnerContact(query.data as OwnerRecord), loading: false };
}
